import { motion } from "framer-motion";
import { ArrowDown, Download, Github, Linkedin, Sparkles } from "lucide-react";
import { SectionContainer } from "@/components/shared/SectionContainer";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/api";

export function HeroSection() {
  return (
    <SectionContainer
      as="section"
      className="relative isolate flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center overflow-hidden pt-24 text-center"
    >
      <DotPatternBackdrop />
      <motion.div
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
        }}
        className="relative z-10 mx-auto flex max-w-5xl flex-col items-center gap-6"
      >
        <motion.div
          variants={fadeUp}
          className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs uppercase tracking-[0.2em] text-primary"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Available for freelance &amp; research collaborations
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="grid h-28 w-28 place-items-center rounded-full bg-gradient-to-br from-primary via-amber-300 to-amber-600 p-[3px] shadow-2xl shadow-primary/30 sm:h-36 sm:w-36"
        >
          <div className="grid h-full w-full place-items-center rounded-full bg-background font-serif text-5xl text-primary sm:text-6xl">
            S
          </div>
        </motion.div>

        <motion.h1
          variants={fadeUp}
          className="font-serif text-5xl leading-[1.05] text-balance text-foreground sm:text-7xl md:text-8xl"
        >
          Salahaldin{" "}
          <span className="bg-gradient-to-r from-primary via-amber-300 to-amber-500 bg-clip-text text-transparent">
            Daraldik
          </span>
        </motion.h1>

        <motion.h2
          variants={fadeUp}
          className="font-sans text-base text-muted-foreground sm:text-lg"
        >
          Software Developer · MSc Data Science · Building at the intersection of{" "}
          <span className="text-foreground">cloud-native apps</span> and{" "}
          <span className="text-foreground">machine learning</span>.
        </motion.h2>

        <motion.p
          variants={fadeUp}
          className="mx-auto max-w-2xl text-sm text-muted-foreground sm:text-base"
        >
          Palestinian developer working from Naples, Italy. Building tools for NGOs, olive-oil
          exporters, and curious minds who&apos;d rather ask their database a question in plain
          English.
        </motion.p>

        <motion.div
          variants={fadeUp}
          className="mt-2 flex flex-wrap items-center justify-center gap-3"
        >
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/20 transition hover:translate-y-[-1px] hover:shadow-xl hover:shadow-primary/30"
          >
            View Projects <ArrowDown className="h-4 w-4" />
          </Link>
          <a
            href={`${import.meta.env.VITE_API_BASE ?? ""}/api/assets/cv/salahaldin-daraldik-cv.pdf`}
            target="_blank"
            rel="noreferrer noopener"
            onClick={() => trackEvent({ eventType: "cv_download" })}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-5 py-2.5 text-sm font-medium text-foreground transition hover:border-primary hover:text-primary"
          >
            <Download className="h-4 w-4" /> Download CV
          </a>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="mt-4 flex items-center gap-2 text-muted-foreground"
        >
          <SocialLink href="https://github.com/SalahAlDin2021" icon={Github} label="GitHub" />
          <SocialLink
            href="https://www.linkedin.com/in/salah-al-din-d-b240491a7/"
            icon={Linkedin}
            label="LinkedIn"
          />
        </motion.div>
      </motion.div>
    </SectionContainer>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

function SocialLink({
  href,
  icon: Icon,
  label,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={label}
      className="grid h-9 w-9 place-items-center rounded-full border border-border transition hover:border-primary hover:text-primary"
    >
      <Icon className="h-4 w-4" />
    </a>
  );
}

function DotPatternBackdrop() {
  return (
    <>
      <div className="absolute inset-0 -z-30 grid-pattern opacity-60" />
      <div className="absolute inset-x-0 top-0 -z-20 h-1/2 bg-gradient-to-b from-primary/10 via-transparent to-transparent" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-transparent to-background" />
    </>
  );
}
